import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	preloadSpaceBlogCountQuery$data,
	preloadSpaceBlogCountQuery$variables,
} from '../__generated__/preloadSpaceBlogCountQuery.graphql.ts';
import preloadSpaceBlogCountQuery from '../__generated__/preloadSpaceBlogCountQuery.graphql.ts';

import { useSimplifiedNavNewUsers } from './useSimplifiedNavNewUsers.ts';

export type SpaceNavD0Overrides = {
	hideShortcuts: boolean;
	hideBlogs: boolean;
	hideCalendar: boolean;
};

type UseSimplifiedSpaceNavParams = {
	spaceKey: string;
	quick: readonly { id: string }[];
};

/**
 * Computes D0 overrides for space navigation item visibility.
 *
 * Returns `null` when the user is not eligible, not in a treatment cohort,
 * or not within the D0 window — meaning "no overrides, use space settings as-is."
 *
 * Returns a `SpaceNavD0Overrides` object when the user is an eligible D0 treatment
 * user, indicating which items should be additionally hidden based on emptiness checks.
 */
export const useSimplifiedSpaceNav = ({
	spaceKey,
	quick,
}: UseSimplifiedSpaceNavParams): SpaceNavD0Overrides | null => {
	const { navStyle, isWithinD0 } = useSimplifiedNavNewUsers();

	const isLoading = navStyle === 'loading';
	const isD0Treatment = navStyle !== 'default' && !isLoading && isWithinD0;

	// Blog count is preloaded by preloadSimplifiedNavForSpaceNav for all eligible
	// D0 users (treatment + control). This query only fires for D0 treatment users,
	// which is a subset of preloaded users, so it's always a cache hit.
	const { data: blogCountData, loading: blogCountLoading } = useQueryInterop<
		preloadSpaceBlogCountQuery$data,
		preloadSpaceBlogCountQuery$variables
	>(preloadSpaceBlogCountQuery, {
		variables: { spaceKey },
		skip: !isD0Treatment,
	});

	if (isLoading) {
		// Preloader FG is at 100% before experiment starts, so loading resolves
		// in one frame (cache hit). Hide items to prevent flash-then-hide for
		// treatment users; the brief hide is imperceptible for control users.
		return { hideShortcuts: true, hideBlogs: true, hideCalendar: true };
	}

	if (!isD0Treatment) {
		return null;
	}

	const totalBlogPosts = blogCountData?.space?.metadata?.totalCurrentBlogPosts;
	const blogCountAvailable = !blogCountLoading && totalBlogPosts != null;

	return {
		hideShortcuts: quick.length === 0,
		hideBlogs: blogCountAvailable ? totalBlogPosts === 0 : false,
		hideCalendar: true,
	};
};
